import dayjs from 'dayjs'
import { StateCreator } from 'zustand'

import {
  Menu,
  MenuGroup,
  MenuLine,
  StockCostResponse,
  IItem,
  IRecipe,
  PriceType,
} from '$models'
import {
  menusService,
  stockCostService,
  recipeService,
  itemService,
} from '$services'
import { exceptionMessageHandler, logger } from '$utils'

import { SharedSlice } from 'src/components/slice'

export type NormalizedGroup = Omit<MenuGroup, 'lines'> & { lineIds: string[] }

export interface MenuEditingState {
  id: string
  name: string
  groupIds: string[]
  groupsById: Record<string, NormalizedGroup>
  linesById: Record<string, MenuLine>
}

interface MenusState {
  menus: Menu[]
  menu: MenuEditingState | null
  _originalMenu: MenuEditingState | null
  loadingStockCosts: boolean
  isSavingMenu: boolean
  recipes: IRecipe[]
  items: IItem[]
  loadingRecipes: boolean
  loadingItems: boolean
  forecastStockCosts: StockCostResponse | null
  liveStockCosts: StockCostResponse | null
}

export interface MenusSlice extends MenusState {
  /**
   * Get all menus
   */
  getMenus: () => Promise<Menu[]>

  /**
   * Get a single menu by id
   * @param menuId the id of the menu to retrieve
   */
  getMenu: (menuId: string) => Promise<Menu | null>

  /**
   * Initializes a new, blank menu in the store for creation
   */
  initializeNewMenu: () => void

  /**
   * Creates the new menu via the API from the current store state
   */
  createMenu: () => Promise<Menu>

  /**
   * Deletes a menu by id
   * @param menuId the id of the menu to delete
   */
  deleteMenu: (menuId: string) => Promise<void>

  /**
   * Get all recipes
   */
  getRecipes: () => Promise<IRecipe[]>

  /**
   * Get all items
   */
  getItems: () => Promise<IItem[]>

  /**
   * Reset the Menus Slice to its initial state
   */
  resetMenusSlice: () => void

  /**
   * Fetches the stock costs for the current menu
   */
  fetchStockCostsForMenu: () => Promise<void>

  /**
   * Updates the local menu object in the store
   * @param menu The entire menu object to update
   */
  updateLocalMenu: (menu: MenuEditingState) => void

  /**
   * Prepares the store for editing a menu by caching the original state
   */
  setMenuForEditing: () => void

  /**
   * Saves the current menu to the backend
   */
  saveMenu: () => Promise<void>

  /**
   * Cancels the edit and restores the original menu state
   */
  cancelMenuEdit: () => void

  /**
   * Updates the name of the menu
   * @param name The new name for the menu
   */
  updateMenuName: (name: string) => void

  /**
   * Updates a menu group
   * @param groupId The id of the group to update
   * @param partialGroup The partial group object with the updated properties
   */
  updateMenuGroup: (
    groupId: string,
    partialGroup: Partial<NormalizedGroup>
  ) => void

  /**
   * Updates a menu line
   * @param lineId The id of the line to update
   * @param partialLine The partial line object with the updated properties
   */
  updateMenuLine: (lineId: string, partialLine: Partial<MenuLine>) => void

  /**
   * Deletes a menu line
   * @param groupId The id of the group that contains the line
   * @param lineId The id of the line to delete
   */
  deleteMenuLine: (groupId: string, lineId: string) => void

  /**
   * Deletes a menu group
   * @param groupId The id of the group to delete
   */
  deleteMenuGroup: (groupId: string) => void

  /**
   * Reorders the lines of a menu group
   * @param groupId The id of the group to reorder
   * @param lineIds The new order of the line ids
   */
  reorderMenuLines: (groupId: string, lineIds: string[]) => void

  /**
   * Moves a menu line to a different group
   * @param lineId The id of the line to move
   * @param sourceGroupId The id of the group that currently contains the line
   * @param destinationGroupId The id of the group to move the line to
   * @param destinationIndex The index in the destination group where the line should be moved to
   */
  moveMenuLineToGroup: (
    lineId: string,
    sourceGroupId: string,
    destinationGroupId: string,
    destinationIndex: number
  ) => void

  /**
   * Adds a new menu line to a group
   * @param groupId The group to add the line to
   * @param recipe The recipe to add (if adding a recipe)
   * @param item The item to add (if adding an item)
   */
  addMenuLine: (groupId: string, recipe?: IRecipe, item?: IItem) => string

  /**
   * Adds a new, empty menu group to the menu
   */
  addMenuGroup: () => void

  /**
   * INTERNAL: Fetch cost data for a single item or recipe and merge it into stockCosts
   */
  _fetchAndMergeStockCosts: (
    itemId?: string,
    recipeId?: string
  ) => Promise<void>
}

const initialState: MenusState = {
  menus: [],
  menu: null,
  _originalMenu: null,
  loadingStockCosts: false,
  isSavingMenu: false,
  recipes: [],
  items: [],
  loadingRecipes: false,
  loadingItems: false,
  forecastStockCosts: null,
  liveStockCosts: null,
}

export const createMenusSlice: StateCreator<
  MenusSlice & SharedSlice,
  [],
  [],
  MenusSlice
> = (set, get) => ({
  ...initialState,

  resetMenusSlice: () => {
    set(initialState)
  },

  getMenus: async (): Promise<Menu[]> => {
    try {
      const menus = await menusService.getAllMenus()
      const menusArray = menus || []
      set({ menus: menusArray })
      return menusArray
    } catch (error) {
      set({ menus: [] })
      exceptionMessageHandler(error, 'Failed to retrieve menus')
      return []
    }
  },

  getMenu: async (menuId: string): Promise<Menu | null> => {
    try {
      const menuFromApi = await menusService.getMenu(menuId)
      if (!menuFromApi) {
        set({ menu: null, _originalMenu: null })
        return null
      }
      const normalizedMenu = normalizeMenu(menuFromApi)
      set({ menu: normalizedMenu, _originalMenu: normalizedMenu })
      return menuFromApi
    } catch (error) {
      set({ menu: null, _originalMenu: null })
      exceptionMessageHandler(error, `Failed to retrieve menu ${menuId}`)
      return null
    }
  },

  initializeNewMenu: () => {
    const newMenu: MenuEditingState = {
      id: window.crypto.randomUUID(),
      name: '',
      groupIds: [],
      groupsById: {},
      linesById: {},
    }
    set({ menu: newMenu, _originalMenu: newMenu })
  },

  createMenu: async () => {
    const menuToCreate = get().menu
    if (!menuToCreate) {
      throw new Error('No menu to create. Call initializeNewMenu first.')
    }
    set({ isSavingMenu: true })
    const apiMenu = denormalizeMenu(menuToCreate)
    try {
      const createdMenu = await menusService.createMenu(apiMenu)
      set((state) => ({
        menus: [...state.menus, createdMenu],
        menu: normalizeMenu(createdMenu),
        _originalMenu: normalizeMenu(createdMenu),
      }))
      return createdMenu
    } catch (error) {
      logger.error('Failed to create menu', error)
      throw error
    } finally {
      set({ isSavingMenu: false })
    }
  },

  deleteMenu: async (menuId: string) => {
    try {
      await menusService.deleteMenu(menuId)
      set((state) => ({
        menus: state.menus.filter((menu) => menu.id !== menuId),
      }))
    } catch (error) {
      logger.error(`Failed to delete menu ${menuId}`, error)
      throw error
    }
  },

  getRecipes: async (): Promise<IRecipe[]> => {
    try {
      const recipes = await recipeService.getAllRecipes()
      set({ recipes })
      return recipes
    } catch (error) {
      logger.error('Failed to retrieve recipes', error)
      set({ recipes: [] })
      return []
    }
  },

  getItems: async (): Promise<IItem[]> => {
    try {
      const items = await itemService.getAllItems()
      set({ items })
      return items
    } catch (error) {
      logger.error('Failed to retrieve items', error)
      set({ items: [] })
      return []
    }
  },

  fetchStockCostsForMenu: async () => {
    const menu = get().menu
    if (!menu) {
      set({ forecastStockCosts: null, liveStockCosts: null })
      return
    }

    set({ loadingStockCosts: true })
    try {
      const liveCostPromise = stockCostService.getStockCostForMenu(
        menu.id,
        PriceType.Live
      )
      const forecastCostPromise = stockCostService.getStockCostForMenu(
        menu.id,
        PriceType.Forecast
      )

      const [forecastStockCosts, liveStockCosts] = await Promise.all([
        forecastCostPromise,
        liveCostPromise,
      ])

      set({
        forecastStockCosts,
        liveStockCosts,
      })
    } catch (error) {
      set({
        forecastStockCosts: null,
        liveStockCosts: null,
      })
      throw error
    } finally {
      set({ loadingStockCosts: false })
    }
  },

  setMenuForEditing: () => {
    set((state) => ({ _originalMenu: state.menu }))
  },

  updateLocalMenu: (menu: MenuEditingState) => {
    set({ menu })
  },

  saveMenu: async () => {
    const menuToSave = get().menu
    if (!menuToSave) return

    set({ isSavingMenu: true })
    const apiMenu = denormalizeMenu(menuToSave)

    try {
      await menusService.updateMenu(apiMenu.id, apiMenu)
      set((state) => ({ _originalMenu: state.menu }))
    } catch (error) {
      logger.error('Failed to save menu', error)
      set((state) => ({ menu: state._originalMenu }))
      throw error
    } finally {
      set({ isSavingMenu: false })
    }
  },

  cancelMenuEdit: () => {
    set((state) => ({ menu: state._originalMenu }))
  },

  updateMenuName: (name: string) => {
    set((state) => {
      if (!state.menu) return {}
      return { menu: { ...state.menu, name } }
    })
  },

  updateMenuGroup: (
    groupId: string,
    partialGroup: Partial<NormalizedGroup>
  ) => {
    set((state) => {
      if (!state.menu) return {}
      const group = state.menu.groupsById[groupId]
      if (!group) return {}
      const updatedGroup = { ...group, ...partialGroup }
      return {
        menu: {
          ...state.menu,
          groupsById: {
            ...state.menu.groupsById,
            [groupId]: updatedGroup,
          },
        },
      }
    })
  },

  updateMenuLine: (lineId: string, partialLine: Partial<MenuLine>) => {
    const previousLine = get().menu?.linesById[lineId]

    set((state) => {
      if (!state.menu) return {}
      const line = state.menu.linesById[lineId]
      if (!line) return {}
      const updatedLine = { ...line, ...partialLine }
      return {
        menu: {
          ...state.menu,
          linesById: {
            ...state.menu.linesById,
            [lineId]: updatedLine,
          },
        },
      }
    })

    const updatedLine = get().menu?.linesById[lineId]

    const recipeChanged = previousLine?.recipeId !== updatedLine?.recipeId
    const itemChanged = previousLine?.stockItemId !== updatedLine?.stockItemId

    if (!recipeChanged && !itemChanged) return

    get()._fetchAndMergeStockCosts(
      updatedLine?.stockItemId,
      updatedLine?.recipeId
    )
  },

  deleteMenuLine: (groupId: string, lineId: string) => {
    set((state) => {
      if (!state.menu) return {}
      const { [lineId]: _, ...remainingLines } = state.menu.linesById
      const group = state.menu.groupsById[groupId]
      if (!group) return {}

      const updatedGroup = {
        ...group,
        lineIds: group.lineIds.filter((id) => id !== lineId),
      }

      return {
        menu: {
          ...state.menu,
          linesById: remainingLines,
          groupsById: {
            ...state.menu.groupsById,
            [groupId]: updatedGroup,
          },
        },
      }
    })
  },

  deleteMenuGroup: (groupId: string) => {
    set((state) => {
      if (!state.menu) return {}
      const group = state.menu.groupsById[groupId]
      if (!group) return {}

      // Remove all lines that belong to this group
      const linesToRemove = new Set(group.lineIds)
      const { ...remainingLines } = state.menu.linesById
      for (const lineId of linesToRemove) {
        delete remainingLines[lineId]
      }

      // Remove the group from groupsById
      const { [groupId]: _, ...remainingGroups } = state.menu.groupsById

      return {
        menu: {
          ...state.menu,
          groupIds: state.menu.groupIds.filter((id) => id !== groupId),
          groupsById: remainingGroups,
          linesById: remainingLines,
        },
      }
    })
  },

  reorderMenuLines: (groupId: string, lineIds: string[]) => {
    set((state) => {
      if (!state.menu) return {}
      const group = state.menu.groupsById[groupId]
      if (!group) return {}

      const updatedGroup = { ...group, lineIds }
      return {
        menu: {
          ...state.menu,
          groupsById: {
            ...state.menu.groupsById,
            [groupId]: updatedGroup,
          },
        },
      }
    })
  },

  moveMenuLineToGroup: (
    lineId: string,
    sourceGroupId: string,
    destinationGroupId: string,
    destinationIndex: number
  ) => {
    set((state) => {
      if (!state.menu) return {}
      const { menu } = state

      const sourceGroup = menu.groupsById[sourceGroupId]
      const destinationGroup = menu.groupsById[destinationGroupId]

      if (!sourceGroup || !destinationGroup) return {}

      const newSourceLineIds = sourceGroup.lineIds.filter((id) => id !== lineId)

      const newDestinationLineIds = [...destinationGroup.lineIds]
      newDestinationLineIds.splice(destinationIndex, 0, lineId)

      const updatedGroups = {
        ...menu.groupsById,
        [sourceGroupId]: {
          ...sourceGroup,
          lineIds: newSourceLineIds,
        },
        [destinationGroupId]: {
          ...destinationGroup,
          lineIds: newDestinationLineIds,
        },
      }

      return {
        menu: {
          ...menu,
          groupsById: updatedGroups,
        },
      }
    })
  },

  addMenuLine: (groupId: string, recipe?: IRecipe, item?: IItem) => {
    const lineId = window.crypto.randomUUID()
    set((state) => {
      if (!state.menu) return {}
      const menu = state.menu

      const newLine: MenuLine = {
        id: lineId,
        workingPrice: 0,
        menuId: menu.id,
        recipeId: recipe?.id,
        stockItemId: item?.id,
        name: recipe?.name || item?.name || '',
        lineOrder: Object.keys(menu.linesById).length,
        stockUnitRatio: undefined,
        salesTaxRateId: undefined,
      }

      const updatedLinesById = {
        ...menu.linesById,
        [newLine.id]: newLine,
      }

      const updatedGroupsById = {
        ...menu.groupsById,
        [groupId]: {
          ...menu.groupsById[groupId],
          lineIds: [...menu.groupsById[groupId].lineIds, newLine.id],
        },
      }

      return {
        menu: {
          ...menu,
          linesById: updatedLinesById,
          groupsById: updatedGroupsById,
        },
      }
    })

    // Trigger cost refresh for the new line
    get()._fetchAndMergeStockCosts(item?.id, recipe?.id)
    return lineId
  },

  addMenuGroup: () => {
    set((state) => {
      if (!state.menu) return {}
      const menu = state.menu
      const newGroupId = window.crypto.randomUUID()
      const newGroup: NormalizedGroup = {
        id: newGroupId,
        name: 'New Section',
        lineIds: [],
      }

      return {
        menu: {
          ...menu,
          groupIds: [...menu.groupIds, newGroupId],
          groupsById: {
            ...menu.groupsById,
            [newGroupId]: newGroup,
          },
        },
      }
    })
  },

  _fetchAndMergeStockCosts: async (
    itemId?: string,
    recipeId?: string
  ): Promise<void> => {
    if (!itemId && !recipeId) return

    try {
      const now = dayjs().toISOString()
      const itemIdTimeStrings = itemId ? [`${itemId},${now}`] : undefined
      const recipeIdTimeStrings = recipeId ? [`${recipeId},${now}`] : undefined

      set({ loadingStockCosts: true })

      const liveCostPromise = stockCostService.getStockCost(
        itemIdTimeStrings,
        recipeIdTimeStrings
      )

      const forecastCostPromise = stockCostService.getStockCost(
        itemIdTimeStrings,
        recipeIdTimeStrings,
        undefined,
        undefined,
        true
      )

      const [forecostCostData, liveCostData] = await Promise.all([
        forecastCostPromise,
        liveCostPromise,
      ])

      set((state) => ({
        liveStockCosts: mergeStockCostResponses(
          state.liveStockCosts,
          liveCostData
        ),
        forecastStockCosts: mergeStockCostResponses(
          state.forecastStockCosts,
          forecostCostData
        ),
      }))
    } catch (error) {
      logger.error('Failed to fetch stock cost', error)
    } finally {
      set({ loadingStockCosts: false })
    }
  },
})

/**
 * Transform a Menu object from the API into the editing-friendly structure
 * used in the Zustand slice. The main difference here is creating lists of
 * ids, and objects keyed by id so we can manage order and also quickly
 * lookup by id.
 */
function normalizeMenu(menuFromApi: Menu): MenuEditingState {
  const linesById: Record<string, MenuLine> = {}
  const groupsById: Record<string, NormalizedGroup> = {}
  const groupIds: string[] = []

  if (menuFromApi.groups) {
    for (const group of menuFromApi.groups) {
      groupIds.push(group.id)
      const lineIds: string[] = []
      if (group.lines) {
        for (const line of group.lines) {
          lineIds.push(line.id)
          linesById[line.id] = line
        }
      }
      const { ...groupWithoutLines } = group
      groupsById[group.id] = { ...groupWithoutLines, lineIds }
    }
  }

  return {
    id: menuFromApi.id,
    name: menuFromApi.name,
    groupIds,
    groupsById,
    linesById,
  }
}

/**
 * Convert the slice editing structure back into the API shape expected by the
 * backend service. This is the reverse of the normalizeMenu function.
 */
function denormalizeMenu(menuState: MenuEditingState): Menu {
  const denormalizedGroups: MenuGroup[] = menuState.groupIds.map((groupId) => {
    const normalizedGroup = menuState.groupsById[groupId]
    const lines: MenuLine[] = normalizedGroup.lineIds.map((lineId, index) => {
      const line = menuState.linesById[lineId]
      return { ...line, lineOrder: index }
    })
    const { ...groupWithoutLineIds } = normalizedGroup
    return { ...groupWithoutLineIds, lines }
  })

  return {
    id: menuState.id,
    name: menuState.name,
    groups: denormalizedGroups,
  }
}

function mergeStockCostResponses(
  existing: StockCostResponse | null,
  incoming: StockCostResponse
): StockCostResponse {
  if (!existing) return incoming

  // Clone existing to keep immutability while preserving prototype where possible
  const merged = Object.assign(
    Object.create(Object.getPrototypeOf(existing)),
    existing
  ) as StockCostResponse

  if (incoming.itemCosts) {
    merged.itemCosts = {
      ...(merged.itemCosts || {}),
      ...incoming.itemCosts,
    }
  }

  if (incoming.recipeCosts) {
    merged.recipeCosts = {
      ...(merged.recipeCosts || {}),
      ...incoming.recipeCosts,
    }
  }

  // Some SDK responses also include variantCosts
  if ((incoming as any).variantCosts) {
    merged.variantCosts = {
      ...((merged as any).variantCosts || {}),
      ...((incoming as any).variantCosts || {}),
    }
  }

  return merged
}

export default createMenusSlice
